import React, { useMemo, useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import InfluencerPendingListRead from '../../components/admin/influencer/InfluencerPendingListRead';
import '../../styles/admin/AdminSummary.css';

const API_URL = process.env.REACT_APP_API_URL;

function buildFallbackUrl(platform, accountName) {
  const name = String(accountName || '').replace(/^@/, '');
  switch (platform) {
    case 'instagram': return `https://www.instagram.com/${name}`;
    case 'tiktok':    return `https://www.tiktok.com/@${name}`;
    case 'youtube':   return `https://www.youtube.com/@${name}`;
    case 'blog':      return `https://www.google.com/search?q=${encodeURIComponent(name + ' 블로그')}`;
    default:          return `https://www.google.com/search?q=${encodeURIComponent(name)}`;
  }
}

function InfluencerRegisterCheckPage() {
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    const targetId = selected?._id || selected?.userId;
    if (!targetId) { setDetail(null); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/influencer/${targetId}`, { credentials: 'include' });
        const data = await res.json();
        if (res.ok) {
          const snsInfo = Array.isArray(data?.snsInfo)
            ? data.snsInfo.map(a => ({
                ...a,
                id: a.id || a._id,
                __draftStatus: ['approved','rejected'].includes(a.status) ? a.status : 'approved',
                __draftFollower: Number.isFinite(Number(a.followerCount)) ? Number(a.followerCount) : 0,
                __draftUrl: a.url || buildFallbackUrl(a.platform, a.accountName)
              }))
            : [];
          setDetail({ ...data, snsInfo });
        } else {
          setDetail(null);
        }
      } catch { setDetail(null); }
      setLoading(false);
    })();
  }, [selected]);

  const sns = useMemo(() => {
    const get = (p) => detail?.snsInfo?.find(s => s.platform === p)?.accountName || '';
    return { tiktok: get('tiktok'), instagram: get('instagram'), blog: get('blog'), youtube: get('youtube') };
  }, [detail]);

  const onChangeSnsStatus = (acc, next) => {
    setDetail(prev => ({
      ...prev,
      snsInfo: (prev?.snsInfo || []).map(a => (a.id || a._id) === (acc.id || acc._id)
        ? {
            ...a,
            __draftStatus: next,
            __draftUrl: next === 'rejected' ? '' : (a.__draftUrl || '')
          }
        : a)
    }));
  };

  const onChangeSnsFollower = (acc, value) => {
    const n = Number(value);
    setDetail(prev => ({
      ...prev,
      snsInfo: (prev?.snsInfo || []).map(a => (a.id || a._id) === (acc.id || acc._id)
        ? { ...a, __draftFollower: Number.isFinite(n) && n >= 0 ? n : '' }
        : a)
    }));
  };

  const onChangeSnsUrl = (acc, value) => {
    setDetail(prev => ({
      ...prev,
      snsInfo: (prev?.snsInfo || []).map(a => (a.id || a._id) === (acc.id || acc._id)
        ? { ...a, __draftUrl: value }
        : a)
    }));
  };

  // URL 유효성 체크(스킴 없으면 https:// 가정)
  const isValidUrl = (s) => {
    if (!s || typeof s !== 'string') return false;
    const t = s.trim();
    if (!t) return false;
    const u = /^https?:\/\//i.test(t) ? t : `https://${t}`;
    try { new URL(u); return true; } catch { return false; }
  };
  const normalizeUrl = (s) => {
    const t = (s || '').trim();
    if (!t) return '';
    return /^https?:\/\//i.test(t) ? t : `https://${t}`;
  };

  // 상태/팔로워/URL 모두 필수(단, URL은 approved일 때만 필수)
  const allReady = useMemo(() => {
    const list = detail?.snsInfo || [];
    if (list.length === 0) return true;
    return list.every(a => {
      const followerOk = a.__draftFollower !== '' && Number.isFinite(Number(a.__draftFollower)) && Number(a.__draftFollower) >= 0;
      const statusOk = ['approved','rejected'].includes(a.__draftStatus);
      const urlOk = a.__draftStatus === 'approved' ? isValidUrl(a.__draftUrl) : true;
      return statusOk && followerOk && urlOk;
    });
  }, [detail]);

  const handleApprove = async () => {
    if (!selected) return;
    if (!allReady) { alert('모든 SNS 계정의 상태, 팔로워 수, URL을 입력/검증해주세요.'); return; }
    const id = selected._id || selected.userId;

    const snsStatuses = (detail?.snsInfo || [])
    .filter(a => a && (a.id || a._id))
    .map(a => ({
      snsId: a.id || a._id,
      status: a.__draftStatus,
      followerCount: Number(a.__draftFollower),
      url: a.__draftStatus === 'approved' ? normalizeUrl(a.__draftUrl) : ''
    }));

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/${id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ snsStatuses })
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setSelected(null);
        setDetail(null);
        setRefreshToken(t => t + 1);
      } else {
        alert(data.message || '승인 실패');
      }
    } catch {
      alert('승인 요청 실패');
    }
    setLoading(false);
  };

  const handleReject = async () => {
    if (!selected) return;
    const id = selected._id || selected.userId;
    if (!window.confirm('해당 가입 요청을 거절하시겠습니까?')) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/${id}/reject`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setSelected(null);
        setDetail(null);
        setRefreshToken(t => t + 1);
      } else {
        alert(data.message || '거절 실패');
      }
    } catch {
      alert('거절 요청 실패');
    }
    setLoading(false);
  };

  const handleDownloadZip = () => {
    const ownerId = selected?._id;
    if (!ownerId) {
      alert('좌측에서 인플루언서를 선택하세요.');
      return;
    }
    const url = `${API_URL}/api/influencer/sns-certi/download-zip?ownerId=${encodeURIComponent(ownerId)}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <AdminLayout title="인플루언서 가입 승인">
      <div className="admin-grid">
        <div className="admin-section" style={{ height: 'calc(100vh - 120px)' }}>
          <InfluencerPendingListRead
            onSelect={(u) => setSelected(u)}
            selectedId={selected?._id || selected?.userId}
            refreshToken={refreshToken}
          />
        </div>

        <div className="admin-section admin-section--wide" style={{ height: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          <h3 className="admin-summary__title">인플루언서 상세 정보</h3>
          {loading && <div className="admin-summary__loading">불러오는 중...</div>}
          {!loading && !detail && <div className="admin-summary__empty" style={{ marginTop: '20px' }}>좌측에서 인플루언서를 선택하세요.</div>}

          {!loading && detail && (
            <>
              <div style={{ lineHeight: 2, fontSize: '14px', color: 'var(--admin-text-body)', marginBottom: '16px' }}>
                <div><strong>인플루언서 ID</strong>: {detail.userId}</div>
                <div><strong>이름</strong>: {detail.userName}</div>
                <div><strong>연락처</strong>: {detail.phoneNumber}</div>
                <div><strong>메일</strong>: {detail.email}</div>
                <div><strong>주소</strong>: {detail.userAddress}</div>
                <div><strong>SNS 계정(틱톡)</strong>: {sns.tiktok}</div>
                <div><strong>SNS 계정(인스타)</strong>: {sns.instagram}</div>
                <div><strong>SNS 계정(블로그)</strong>: {sns.blog}</div>
                <div><strong>SNS 계정(유튜브)</strong>: {sns.youtube}</div>
              </div>

              {Array.isArray(detail.snsInfo) && detail.snsInfo.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '14px', color: 'var(--admin-text-main)' }}>SNS 계정 상태/팔로워/URL</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {detail.snsInfo.map(acc => (
                      <li key={(acc.id || acc._id) + acc.platform} style={{ padding: '12px', marginBottom: '8px', background: 'var(--admin-bg-body)', borderRadius: '6px', border: '1px solid var(--admin-border-color)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto minmax(220px, 320px)', gap: 12, alignItems: 'center' }}>
                          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-main)' }}>[{acc.platform}] @{acc.accountName}</span>

                          <span>
                            <label style={{ marginRight: 8, fontSize: '14px' }}>
                              <input
                                type="radio"
                                name={`sns-status-${acc.id || acc._id}`}
                                value="approved"
                                checked={(acc.__draftStatus || 'approved') === 'approved'}
                                onChange={(e) => onChangeSnsStatus(acc, e.target.value)}
                              /> 승인
                            </label>
                            <label style={{ fontSize: '14px' }}>
                              <input
                                type="radio"
                                name={`sns-status-${acc.id || acc._id}`}
                                value="rejected"
                                checked={acc.__draftStatus === 'rejected'}
                                onChange={(e) => onChangeSnsStatus(acc, e.target.value)}
                              /> 반려
                            </label>
                          </span>

                          <span style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, alignItems:'center' }}>
                            <input
                              type="number"
                              min={0}
                              value={acc.__draftFollower}
                              onChange={(e) => onChangeSnsFollower(acc, e.target.value)}
                              style={{ width: 120, height: 32, padding: '4px 8px', border: '1px solid var(--admin-border-color)', borderRadius: '4px', fontSize: '14px' }}
                            />
                            <input
                              type="text"
                              placeholder={acc.__draftStatus === 'rejected' ? '반려 시 URL 불필요' : 'https://...'}
                              value={acc.__draftUrl}
                              onChange={(e) => onChangeSnsUrl(acc, e.target.value)}
                              disabled={acc.__draftStatus === 'rejected'}
                              style={{ width: '100%', height: 32, padding: '4px 8px', border: '1px solid var(--admin-border-color)', borderRadius: '4px', fontSize: '14px', background: acc.__draftStatus === 'rejected' ? '#f5f5f5' : 'var(--admin-bg-input)' }}
                            />
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="admin-section__actions" style={{ justifyContent: 'flex-end', marginTop: 20 }}>
                <button
                  className="admin-section__button admin-section__button--secondary"
                  type="button"
                  onClick={handleDownloadZip}
                  disabled={loading || !selected}
                >
                  첨부 이미지 다운로드
                </button>

                <button
                  className="admin-section__button"
                  type="button"
                  onClick={handleReject}
                  disabled={loading}
                  style={{ backgroundColor: loading ? '#ef9a9a' : 'var(--admin-color-danger)', borderColor: loading ? '#ffcdd2' : 'var(--admin-color-danger)' }}
                >
                  가입 거절
                </button>

                <button
                  className="admin-section__button"
                  type="button"
                  onClick={handleApprove}
                  disabled={loading || !allReady}
                  style={{ backgroundColor: loading || !allReady ? '#b2dfdb' : 'var(--admin-color-success)', borderColor: loading || !allReady ? '#80cbc4' : 'var(--admin-color-success)' }}
                >
                  {loading ? '처리 중...' : (allReady ? '가입 승인' : '필수 항목 미완료')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default InfluencerRegisterCheckPage;